import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  AuthResponse, 
  LoginRequest, 
  Donor, 
  CreateDonorRequest, 
  Donation, 
  CreateDonationRequest, 
  Receipt, 
  DonationStats, 
  PaginatedResponse, 
  Filters,
  Collector,
  CreateCollectorRequest,
  CollectorStats,
  Team,
  CreateTeamRequest
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // ERRORE: Fallback a /api non funziona in produzione
    // this.baseURL = import.meta.env.VITE_API_URL || '/api';
    // CORRETTO: Richiede VITE_API_URL definito
    this.baseURL = import.meta.env.VITE_API_URL;
    if (!this.baseURL) {
      throw new Error('VITE_API_URL environment variable is required');
    }
    
    this.api = axios.create({
      baseURL: this.baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor per aggiungere token alle richieste
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Interceptor per gestire refresh token
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error.response?.status === 401) {
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const response = await this.refreshToken(refreshToken);
              localStorage.setItem('access_token', response.data.access_token);
              localStorage.setItem('refresh_token', response.data.refresh_token);
              
              // Riprova la richiesta originale
              error.config.headers.Authorization = `Bearer ${response.data.access_token}`;
              return this.api.request(error.config);
            } catch (refreshError) {
              // Refresh fallito, redirect al login
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<AxiosResponse<AuthResponse>> {
    return this.api.post('/auth/refresh', { refresh_token: refreshToken });
  }

  async getProfile() {
    const response = await this.api.get('/auth/profile');
    return response.data;
  }

  // Health check
  async getHealth() {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Donors endpoints
  async getDonors(filters: Filters = {}): Promise<PaginatedResponse<Donor>> {
    const response = await this.api.get('/donors', { params: filters });
    return response.data;
  }

  async getDonor(id: string): Promise<Donor> {
    const response = await this.api.get(`/donors/${id}`);
    return response.data;
  }

  async createDonor(donor: CreateDonorRequest): Promise<Donor> {
    const response = await this.api.post('/donors', donor);
    return response.data;
  }

  async updateDonor(id: string, donor: Partial<CreateDonorRequest>): Promise<Donor> {
    const response = await this.api.put(`/donors/${id}`, donor);
    return response.data;
  }

  async deleteDonor(id: string): Promise<void> {
    await this.api.delete(`/donors/${id}`);
  }

  async getDonorDonations(id: string, filters: Filters = {}): Promise<PaginatedResponse<Donation>> {
    const response = await this.api.get(`/donors/${id}/donations`, { params: filters });
    return response.data;
  }

  async getDonorAutocompleteOptions(): Promise<Donor[]> {
    const response = await this.api.get('/donors/autocomplete');
    return response.data;
  }

  // Donations endpoints
  async getDonations(filters: Filters = {}): Promise<PaginatedResponse<Donation>> {
    const response = await this.api.get('/donations', { params: filters });
    return response.data;
  }

  async getDonation(id: string): Promise<Donation> {
    const response = await this.api.get(`/donations/${id}`);
    return response.data;
  }

  async createDonation(donation: CreateDonationRequest): Promise<Donation> {
    const response = await this.api.post('/donations', donation);
    return response.data;
  }

  async updateDonation(id: string, donation: Partial<CreateDonationRequest>): Promise<Donation> {
    const response = await this.api.put(`/donations/${id}`, donation);
    return response.data;
  }

  async deleteDonation(id: string): Promise<void> {
    await this.api.delete(`/donations/${id}`);
  }

  async getDonationsByCollector(collectorId: string, filters: Filters = {}): Promise<PaginatedResponse<Donation>> {
    const response = await this.api.get(`/donations/collector/${collectorId}`, { params: filters });
    return response.data;
  }

  async getDonationStats(): Promise<DonationStats> {
    const response = await this.api.get('/donations/stats/summary');
    return response.data;
  }

  // Receipts endpoints
  async getReceipt(id: string): Promise<Receipt> {
    const response = await this.api.get(`/receipts/${id}`);
    return response.data;
  }

  async getReceiptDownloadUrl(id: string): Promise<{ downloadUrl: string; expiresIn: number }> {
    const response = await this.api.get(`/receipts/${id}/download`);
    return response.data;
  }

  async getReceiptFileBlob(id: string): Promise<Blob> {
    const response = await this.api.get(`/receipts/${id}/file`, {
      responseType: 'blob',
    });
    return new Blob([response.data], { type: 'application/pdf' });
  }

  async downloadReceiptFile(id: string, originalName?: string): Promise<void> {
    const response = await this.api.get(`/receipts/${id}/file`, {
      responseType: 'blob',
    });
    
    // Crea un blob URL e scarica il file
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Usa il nome originale se fornito, altrimenti fallback
    const fileName = originalName || `receipt-${id}.pdf`;
    
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async deleteReceipt(id: string): Promise<void> {
    await this.api.delete(`/receipts/${id}`);
  }

  // Upload receipt
  async uploadReceipt(file: File, donationId: string, onProgress?: (progress: number) => void): Promise<Receipt> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('donationId', donationId);

    const response = await this.api.post('/receipts/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Collectors API
  async getCollectors(filters: Filters = {}): Promise<PaginatedResponse<Collector>> {
    const response = await this.api.get('/collectors', { params: filters });
    return response.data;
  }

  async getCollector(id: string): Promise<Collector> {
    const response = await this.api.get(`/collectors/${id}`);
    return response.data;
  }

  async createCollector(collector: CreateCollectorRequest): Promise<Collector> {
    const response = await this.api.post('/collectors', collector);
    return response.data;
  }

  async updateCollector(id: string, collector: Partial<CreateCollectorRequest>): Promise<Collector> {
    const response = await this.api.patch(`/collectors/${id}`, collector);
    return response.data;
  }

  async deleteCollector(id: string): Promise<void> {
    await this.api.delete(`/collectors/${id}`);
  }

  async getCollectorStats(): Promise<CollectorStats> {
    const response = await this.api.get('/collectors/stats');
    return response.data;
  }

  // S3 Upload methods
  async uploadDocumentPhoto(formData: FormData): Promise<{
    s3Key: string;
    s3Url: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
  }> {
    const response = await this.api.post('/s3/upload/document-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // S3 Signed URL methods
  async getSignedUrl(s3Key: string, expiresIn: number = 3600): Promise<string> {
    const response = await this.api.get(`/s3/signed-url?key=${encodeURIComponent(s3Key)}&expiresIn=${expiresIn}`);
    return response.data.signedUrl;
  }

  // S3 Proxy methods
  getImageUrl(s3Key: string): string {
    // Ottieni il token JWT dal localStorage
    const token = localStorage.getItem('access_token');
    const baseUrl = this.api.defaults.baseURL;
    
    if (token) {
      return `${baseUrl}/s3/image?key=${encodeURIComponent(s3Key)}&token=${encodeURIComponent(token)}`;
    } else {
      // Fallback senza token (potrebbe non funzionare)
      return `${baseUrl}/s3/image?key=${encodeURIComponent(s3Key)}`;
    }
  }

  // S3 Staging methods
  async commitStagingFile(stagingKey: string, finalKey: string): Promise<{
    s3Key: string;
    s3Url: string;
  }> {
    const response = await this.api.post('/s3/commit-staging', {
      stagingKey,
      finalKey,
    });
    return response.data;
  }

  async rollbackStagingFile(stagingKey: string): Promise<{ message: string }> {
    const response = await this.api.delete('/s3/rollback-staging', {
      data: { stagingKey },
    });
    return response.data;
  }

  async cleanupStagingFiles(olderThanHours: number = 24): Promise<{
    deletedCount: number;
    message: string;
  }> {
    const response = await this.api.post('/s3/cleanup-staging', {
      olderThanHours,
    });
    return response.data;
  }

  async getStagingStats(): Promise<{
    totalFiles: number;
    totalSize: number;
    oldestFile?: string;
  }> {
    const response = await this.api.get('/s3/staging-stats');
    return response.data;
  }

  // Donation Receipts methods
  async deleteDonationReceipts(donationId: string): Promise<{
    deletedCount: number;
    deletedReceipts: string[];
    message: string;
  }> {
    const response = await this.api.delete(`/donations/${donationId}/receipts`);
    return response.data;
  }

  // Settings/Templates methods
  async getTemplates(): Promise<any[]> {
    const response = await this.api.get('/templates');
    return response.data;
  }

  async getReceiptTemplate(): Promise<{ template: string }> {
    const response = await this.api.get('/receipts/template');
    return response.data;
  }

  async updateReceiptTemplate(template: string): Promise<{ message: string; template: string }> {
    const response = await this.api.put('/receipts/template', { template });
    return response.data;
  }

  // Template email ricevuta
  async getReceiptEmailTemplate(): Promise<{ template: string }> {
    const response = await this.api.get('/templates/receipt-email');
    return response.data;
  }

  async updateReceiptEmailTemplate(template: string): Promise<{ message: string; template: string }> {
    const response = await this.api.put('/templates/receipt-email', { template });
    return response.data;
  }

  // Receipt Email endpoints
  async sendReceiptEmail(donationId: string): Promise<{ success: boolean; message: string; emailLogId?: string }> {
    const response = await this.api.post(`/receipt-email/send/${donationId}`);
    return response.data;
  }

  async sendPendingReceiptEmails(): Promise<{ sent: number; failed: number; errors: string[] }> {
    const response = await this.api.post('/receipt-email/send-pending');
    return response.data;
  }

  async getEmailStatus(donationId: string): Promise<{
    hasReceivedEmail: boolean;
    lastEmailStatus?: string;
    lastEmailDate?: string;
    errorMessage?: string;
    retryCount?: number;
    maxRetries?: number;
    emailLogId?: string;
    totalEmails?: number;
  }> {
    const response = await this.api.get(`/receipt-email/status/${donationId}`);
    return response.data;
  }

  async retryFailedEmail(emailLogId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post(`/receipt-email/retry/${emailLogId}`);
    return response.data;
  }

  async getEmailLogsForDonation(donationId: string): Promise<{
    logs: Array<{
      id: string;
      recipientEmail: string;
      subject: string;
      status: string;
      errorMessage?: string;
      sentAt?: string;
      retryCount: number;
      maxRetries: number;
      createdAt: string;
    }>;
  }> {
    const response = await this.api.get(`/receipt-email/logs/${donationId}`);
    return response.data;
  }

  // Template management
  async updateTemplate(templateId: string, content: string): Promise<{ message: string; template: any }> {
    const response = await this.api.put(`/templates/${templateId}`, { content });
    return response.data;
  }

  // Teams endpoints
  async getTeams(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<PaginatedResponse<Team>> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.isActive !== undefined && filters.isActive !== null) params.append('isActive', filters.isActive.toString());

    const response = await this.api.get(`/teams?${params.toString()}`);
    return response.data;
  }

  async getTeam(id: string): Promise<Team> {
    const response = await this.api.get(`/teams/${id}`);
    return response.data;
  }

  async createTeam(team: CreateTeamRequest): Promise<Team> {
    const response = await this.api.post('/teams', team);
    return response.data;
  }

  async updateTeam(id: string, team: Partial<CreateTeamRequest>): Promise<Team> {
    const response = await this.api.patch(`/teams/${id}`, team);
    return response.data;
  }

  async deleteTeam(id: string): Promise<void> {
    await this.api.delete(`/teams/${id}`);
  }

  // Fiscal validation endpoints
  async validateFiscalCode(fiscalCode: string): Promise<{ isValid: boolean; error?: string }> {
    const response = await this.api.post('/fiscal-validation/validate-fiscal-code', { fiscalCode });
    return response.data;
  }

  async validateVatNumber(vatNumber: string): Promise<{ isValid: boolean; error?: string }> {
    const response = await this.api.post('/fiscal-validation/validate-vat-number', { vatNumber });
    return response.data;
  }

  async validateSdiCode(sdiCode: string): Promise<{ isValid: boolean; error?: string }> {
    const response = await this.api.post('/fiscal-validation/validate-sdi-code', { sdiCode });
    return response.data;
  }

  async extractInfoFromFiscalCode(fiscalCode: string): Promise<{
    isValid: boolean;
    data?: {
      surname: string;
      name: string;
      birthYear: number;
      birthMonth: number;
      birthDay: number;
      gender: 'M' | 'F';
      birthPlace: string;
    };
    error?: string;
  }> {
    const response = await this.api.post('/fiscal-validation/extract-info-from-fiscal-code', { fiscalCode });
    return response.data;
  }

  async getCompanyInfoByVatNumber(vatNumber: string): Promise<{
    isValid: boolean;
    data?: {
      companyName: string;
      address: string;
      city: string;
      postalCode: string;
      province: string;
    };
    error?: string;
  }> {
    const response = await this.api.post('/fiscal-validation/get-company-info', { vatNumber });
    return response.data;
  }

  // Endpoint reali per validazione fiscale
  async validateFiscalCodeReal(fiscalCode: string): Promise<{ isValid: boolean; error?: string }> {
    const response = await this.api.post('/fiscal-validation/validate-fiscal-code-real', { fiscalCode });
    return response.data;
  }

  async validateVatNumberReal(vatNumber: string): Promise<{ isValid: boolean; error?: string }> {
    const response = await this.api.post('/fiscal-validation/validate-vat-number-real', { vatNumber });
    return response.data;
  }

  async validateSdiCodeReal(sdiCode: string): Promise<{ isValid: boolean; error?: string }> {
    const response = await this.api.post('/fiscal-validation/validate-sdi-code-real', { sdiCode });
    return response.data;
  }

}

export const apiService = new ApiService();
export default apiService;
